<template>
  <div class="md-layout md-gutter">
    <div class="md-layout-item">
      <transition @enter="scaleUp">
        <md-card
          class="card-title"
          style="max-width: 768px; margin-top: 25px"
          v-if="exam.name"
          :key="exam.name"
          data-index="0"
        >
          <md-card-header>
            <span class="md-display-1">{{ exam.name }}</span>
          </md-card-header>
          <md-divider></md-divider>
          <md-card-content>
            <div class="md-subheading text-justify">{{ exam.description }}</div>
          </md-card-content>
        </md-card>
      </transition>

      <md-card
        class="card-question"
        v-for="(question, index) in exam.questions"
        v-bind:key="question.id"
        v-bind:data-index="index + 1"
        v-bind:class="[
          answers[question.id].correctionMark == 'Correct'
            ? 'card-question-correct'
            : '',
          answers[question.id].correctionMark == 'Incorrect'
            ? 'card-question-incorrect'
            : '',
        ]"
        style="max-width: 768px"
      >
        <md-card-header>
          <p class="md-subheading text-justify">
            {{ question.number }}. {{ question.question }}
          </p>
        </md-card-header>
        <md-divider></md-divider>
        <md-card-content>
          <div v-if="question.questionType == 'MultipleChoice'">
            <div v-for="choice in question.choices" v-bind:key="choice.number"  v-bind:class="[
                      answers[question.id].answerChoiceId == choice.id
                        ? 'correct-choice-highlight'
                        : '',
                    ]">
              <md-radio
                class="md-primary"
                v-model="choices[choice.id]"
                disabled
              >
                <div class="text-justify">
                  <span>{{ formatChoiceLetter(choice) }}.</span>
                  <span class="choice-text">{{ formatChoiceText(choice) }}</span>
                </div>
              </md-radio>
            </div>
          </div>
          <div v-else>
              <p class="text-justify">{{answers[question.id].text}}</p>
              <span class="md-count">{{ answers[question.id].wordCount || 0 }} of 500 words</span>
          </div>
        </md-card-content>
        <md-card-expand>
          <md-card-actions md-alignment="space-between">
            <div></div>
            <md-card-expand-trigger>
              <md-button>Guide</md-button>
            </md-card-expand-trigger>
          </md-card-actions>

          <md-card-expand-content>
            <md-card-content class="md-caption">
              <p class="text-indent">{{ answers[question.id].guide }}</p>
            </md-card-content>
          </md-card-expand-content>
        </md-card-expand>
      </md-card>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import axios from "axios";
import { gsap } from "gsap";
import GroupTransition from "@/components/GroupTransition.vue";

export default {
  name: "ReviewExam",
  components: {
    GroupTransition,
  },
  data: () => ({
    formMode: "preview",
    submitting: false,
    id: -1,
    answers: {},
    choices: {},
    text: {},
    wordCounts: {},
    exam: {
      id: -1,
      name: "",
      description: "",
      questions: [],
    },
    model: {
      id: -1,
      examId: -1,
      state: "",
      answers: [],
    },
    observer: null,
  }),
  methods: {
    async loadAssignment() {
      const response = await axios.get(`api/student-work/${this.id}/review`);

      var answers = response.data.answers;

      for (var i = 0; i < answers.length; i++) {
        var answer = answers[i];

        if (answer.type == "MultipleChoice")
          this.choices[answer.choiceId] = "on";

        this.answers[answers[i].questionId] = answer;
      }

      this.model = response.data;
    },
    async loadExam() {
      const response = await axios.get(`api/exams/${this.model.examId}`, {
        params: { _: Date.now() },
      });

      this.exam = response.data;
    },
    formatChoiceLetter(choice) {
      return String.fromCharCode(64 + choice.number);
    },
    formatChoiceText(choice)
    {
      if (choice.text && !choice.multiselect)
        return choice.text;

      var text = choice.multiselect;
      var input = text.split(",");
      var output = [];

      for (var i = input.length; i >= 0; i--) {
        var number = parseInt(input[i]);

        if (number) output.unshift(String.fromCharCode(64 + number));
      }

      // formatting for commas in 'A, B, and C'
      if (output.length > 2)
        for (var i = 1; i < output.length; i += 2) output.splice(i, 0, ", ");
      // formatting for the word 'Both' in 'Both A and B'
      else output.splice(0, 0, "Both ");

      // formatting for the 'and' in 'A and B'
      output.splice(output.length - 1, 0, " and ");

      return output.join("");
    },
    scaleUp(element) {
      gsap.fromTo(
        element,
        {
          scale: 0.95,
          opacity: 0,
          transformOrigin: "bottom",
        },
        {
          scale: 1,
          opacity: 1,
          duration: 0.3,
          ease: "power3.inOut",
        }
      );
    },
    attachObserver() {
      const rootElement = this.$el;
      const questions = rootElement.querySelectorAll(".card-question");

      this.observer = new IntersectionObserver(this.onElementObserved, {
        root: this.$refs.rootElement,
      });

      questions.forEach((question) => this.observer.observe(question));
    },
    onElementObserved(entries) {
      entries.forEach((entry) => {
        const element = entry.target;

        if (entry.isIntersecting) {
          const elementIsVisible = element.classList.contains("visible");

          if (!elementIsVisible) {
            this.scaleUp(element);
            element.classList.add("visible");
          }
        }
      });
    },
  },
  async created() {
    this.id = this.$route.params.id;
    await this.loadAssignment();
    await this.loadExam();
    this.$nextTick(this.attachObserver);
  },
};
</script>

<style lang="scss" scoped>

.choice-text
{
    padding-left: 1em;
}

.back-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
}

.card-title {
  border-top: 10px solid rgb(103, 58, 183);
  margin-left: auto;
  margin-right: auto;
}

.card-question {
  opacity: 0;
  border-left: 10px solid rgb(255, 255, 255);
  margin: 55px;
  margin-left: auto;
  margin-right: auto;
}

.correct-choice-highlight {
  font-weight: bold;
  margin: 0 -0.4em;
  padding: 0.1em 0.4em;
  border-radius: 0.8em 0.3em;
  background-color: green;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}

.card-question-correct {
  border-left: 10px solid green;
}

.card-question-incorrect {
  border-left: 10px solid red;
}

.md-card {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  padding: 15px;
}

.text-justify {
  text-align: justify;
}
</style>
